import {
	ELEVATION_SAMPLE_INTERVAL_MILES,
	GRADE_FACTOR_HEAVY,
	GRADE_FACTOR_LIGHT,
	GRADE_RECOVERY_FACTOR,
	MPG_CEILING_RATIO,
	MPG_FLOOR_RATIO,
	NATIONAL_AVERAGE_PRICES,
} from './constants'
import type { ElevationPoint, FuelType, StateFuelPrices, StateSegment } from './types'

/**
 * Calculate elevation-adjusted MPG for a single segment.
 *
 * The grade percentage determines how much fuel consumption changes:
 * - Uphill: fuel consumption increases (lower MPG)
 * - Downhill: fuel consumption decreases slightly (higher MPG, capped)
 * - Heavy vehicles (buses, trucks) are more affected than light vehicles
 */
export function adjustMpgForGrade(
	baseMpg: number,
	gradePercent: number,
	weightClass: 'light' | 'heavy',
): number {
	const gradeFactor = weightClass === 'heavy' ? GRADE_FACTOR_HEAVY : GRADE_FACTOR_LIGHT
	const mpgFloor = baseMpg * MPG_FLOOR_RATIO
	const mpgCeiling = baseMpg * MPG_CEILING_RATIO

	let adjustedMpg: number

	if (gradePercent > 0) {
		// Uphill — penalty increases with grade
		adjustedMpg = baseMpg * (1 - gradeFactor * gradePercent)
	} else {
		// Downhill — modest recovery (engine braking limits savings)
		adjustedMpg = baseMpg * (1 + GRADE_RECOVERY_FACTOR * Math.abs(gradePercent))
	}

	// Clamp to floor/ceiling
	return Math.max(mpgFloor, Math.min(mpgCeiling, adjustedMpg))
}

/**
 * Calculate grade percentage between two elevation points.
 * Grade = (rise / run) * 100
 *
 * @param elevationChangeFeet - elevation change in feet
 * @param horizontalDistanceMiles - horizontal distance in miles
 * @returns grade as a percentage (positive = uphill, negative = downhill)
 */
export function calculateGrade(
	elevationChangeFeet: number,
	horizontalDistanceMiles: number,
): number {
	if (horizontalDistanceMiles <= 0) return 0
	const horizontalDistanceFeet = horizontalDistanceMiles * 5280
	return (elevationChangeFeet / horizontalDistanceFeet) * 100
}

/**
 * Core fuel estimation engine.
 *
 * Takes the route's elevation profile, splits it by state, adjusts MPG per
 * segment based on grade, and calculates per-state fuel cost using real prices.
 *
 * @param elevationProfile - elevation samples along the route
 * @param stateAtPoints - state code for each elevation sample point
 * @param baseMpg - vehicle's base MPG on flat terrain
 * @param weightClass - vehicle weight class (affects grade penalty)
 * @param fuelType - diesel or gas
 * @param fuelPrices - per-state fuel prices
 * @returns per-state breakdown with gallons, cost, and effective MPG
 */
export function calculateFuelEstimate(
	elevationProfile: ElevationPoint[],
	stateAtPoints: string[],
	baseMpg: number,
	weightClass: 'light' | 'heavy',
	fuelType: FuelType,
	fuelPrices: StateFuelPrices,
): StateSegment[] {
	if (elevationProfile.length < 2) return []

	// Accumulate per-state data
	const stateData: Record<string, { distanceMiles: number; gallons: number }> = {}

	for (let i = 1; i < elevationProfile.length; i++) {
		const prev = elevationProfile[i - 1] as ElevationPoint
		const curr = elevationProfile[i] as ElevationPoint
		const segmentDistance = curr.distance - prev.distance
		const state = stateAtPoints[i] ?? stateAtPoints[i - 1] ?? 'US'

		if (segmentDistance <= 0) continue

		// Calculate grade for this segment
		const elevationChange = curr.elevation - prev.elevation
		const grade = calculateGrade(elevationChange, segmentDistance)

		// Adjust MPG based on grade and vehicle weight
		const effectiveMpg = adjustMpgForGrade(baseMpg, grade, weightClass)

		// Calculate gallons for this segment
		const segmentGallons = segmentDistance / effectiveMpg

		// Accumulate into state bucket
		if (!stateData[state]) {
			stateData[state] = { distanceMiles: 0, gallons: 0 }
		}
		stateData[state].distanceMiles += segmentDistance
		stateData[state].gallons += segmentGallons
	}

	// Build state segments with pricing
	const segments: StateSegment[] = Object.entries(stateData).map(([stateCode, data]) => {
		const prices = fuelPrices[stateCode]
		const price = prices ? prices[fuelType] : NATIONAL_AVERAGE_PRICES[fuelType]

		return {
			state: stateCode,
			stateCode,
			distanceMiles: Math.round(data.distanceMiles * 10) / 10,
			gallons: Math.round(data.gallons * 10) / 10,
			fuelPrice: price,
			cost: Math.round(data.gallons * price * 100) / 100,
			effectiveMpg: Math.round((data.distanceMiles / data.gallons) * 10) / 10,
		}
	})

	return segments
}

/**
 * Sample points along a GeoJSON LineString at regular distance intervals
 * (every `ELEVATION_SAMPLE_INTERVAL_MILES`, 5 miles by default).
 *
 * **Distance-based, not vertex-based.** A prior version stepped evenly
 * through the vertex index array, which produced clustered samples
 * wherever Mapbox emitted dense polyline vertices (typically in cities
 * and on interstate interchanges) and sparse samples elsewhere — exactly
 * the opposite of what the documentation claimed. Grades and state-
 * attribution were noisy on any route with uneven vertex density.
 *
 * Implementation:
 *   1. Walk the polyline once, computing cumulative distance at each
 *      vertex via haversine.
 *   2. For each target sample distance (0, 5, 10, ... totalDistanceMiles),
 *      binary-search the vertex pair that brackets it and linearly
 *      interpolate the lng/lat between them.
 *   3. Always include the start vertex (distance 0) and the end vertex
 *      (distance = totalDistanceMiles) so the profile covers the full
 *      route even when the interval doesn't divide evenly.
 *
 * Returned distances are measured in miles from the route start.
 */
/** Pass 1: cumulative haversine distance at every vertex of a polyline. */
function cumulativeDistancesMi(coords: ReadonlyArray<GeoJSON.Position>): number[] {
	const cumulative: number[] = new Array(coords.length)
	cumulative[0] = 0
	for (let i = 1; i < coords.length; i++) {
		const prev = coords[i - 1] as [number, number]
		const curr = coords[i] as [number, number]
		const segmentMiles = haversineDistance(prev[1], prev[0], curr[1], curr[0])
		cumulative[i] = (cumulative[i - 1] ?? 0) + segmentMiles
	}
	return cumulative
}

/**
 * Pass 2 helper: given a target distance along the polyline, return the
 * interpolated (lng, lat) at that point. `searchStart` is mutated forward
 * so the caller's outer loop only scans each vertex once across N calls.
 */
function interpolateAt(
	coords: ReadonlyArray<GeoJSON.Position>,
	cumulative: ReadonlyArray<number>,
	target: number,
	searchStart: { value: number },
): { lng: number; lat: number } {
	// Walk forward from the last-found segment — cumulative distances are
	// monotonically increasing, so we never need to search backward.
	while (
		searchStart.value < coords.length - 1 &&
		(cumulative[searchStart.value + 1] ?? 0) < target
	) {
		searchStart.value++
	}
	const startIdx = searchStart.value
	const endIdx = Math.min(startIdx + 1, coords.length - 1)

	const startCoord = coords[startIdx] as [number, number]
	const endCoord = coords[endIdx] as [number, number]
	const startDist = cumulative[startIdx] ?? 0
	const endDist = cumulative[endIdx] ?? startDist

	// Linear interpolation between the bracketing vertices. Small enough
	// segments that a great-circle correction isn't needed for elevation
	// lookup purposes — the error is well under Open-Meteo's native
	// 30-90m resolution.
	const segmentSpan = endDist - startDist
	const t = segmentSpan > 0 ? (target - startDist) / segmentSpan : 0
	return {
		lng: startCoord[0] + (endCoord[0] - startCoord[0]) * t,
		lat: startCoord[1] + (endCoord[1] - startCoord[1]) * t,
	}
}

/**
 * Pick a sampling interval based on total route length. Adaptive because the
 * two elevation upstreams (Open-Meteo + Mapbox Tilequery fallback) both have
 * per-IP rate limits we share with every other Render customer:
 *
 *   - Open-Meteo: soft per-IP budget that 429s coast-to-coast runs at 5mi
 *     sampling (663 coords / 7 batches in < 15s consistently trips it).
 *   - Mapbox Tilequery: 600 req/min per-token. Our fallback fires 10
 *     concurrent per batch with a 600ms gap; every additional batch adds
 *     ~600ms of floor latency, so cutting coord count directly cuts
 *     response time on long routes where Mapbox fallback is engaged.
 *
 * Halving the coord count on long routes halves the pressure on BOTH
 * providers simultaneously. Grade precision trade-off is tiny at this
 * scale: mountain passes are 10-30mi of climb, so even 25mi sampling
 * still gets 1 sample across a typical pass. The smoothing from grade-
 * adjusted segment MPG absorbs any remainder.
 *
 * Route-length buckets chosen from observed failure modes:
 *   - ≤ 500mi:     5mi — current behaviour, no change for 90% of traffic
 *   - 500-1500:    7mi — regional tours (LA→SF, NYC→Chicago)
 *   - 1500-3000:   10mi — cross-region (NYC→Denver, Seattle→Miami)
 *   - 3000-4500:   20mi — coast-to-coast (Portland ME→LA, Seattle→Miami)
 *   - > 4500:      25mi — absurd routes (round trips, multi-leg tours)
 *
 * Live metrics that drove the 3000+ tier split (2026-04-19 evening):
 *   Pre-fix:   Portland ME→LA at 5mi = 36.9s → client timeout (499)
 *   Post-15mi: Portland OR→Miami at 15mi = 15.0s, succeeded via Mapbox
 *   Target:    <12s on coast-to-coast via 20mi sampling
 *
 * Explicit export so the constant `ELEVATION_SAMPLE_INTERVAL_MILES` can
 * stay in place as the short-route default without becoming dead code.
 */
export function adaptiveSamplingInterval(totalMiles: number): number {
	if (totalMiles > 4500) return 25
	if (totalMiles > 3000) return 20
	if (totalMiles > 1500) return 10
	if (totalMiles > 500) return 7
	return ELEVATION_SAMPLE_INTERVAL_MILES
}

export function sampleRoutePoints(
	geometry: GeoJSON.LineString,
	totalDistanceMiles: number,
	intervalMilesOverride?: number,
): Array<{ lng: number; lat: number; distanceMiles: number }> {
	const coords = geometry.coordinates
	if (coords.length < 2) return []

	const cumulative = cumulativeDistancesMi(coords)
	const polylineMiles = cumulative[cumulative.length - 1] ?? 0

	// Use the polyline's own length rather than the passed totalDistanceMiles
	// when interpolating — they're usually identical, but the polyline is
	// self-consistent (haversine of our vertices) and avoids drift between
	// "distance reported by Mapbox" and "distance we compute from geometry."
	// We still return the caller's totalDistanceMiles in the last sample so
	// downstream consumers see a consistent total.
	const effectiveMiles = polylineMiles > 0 ? polylineMiles : totalDistanceMiles
	// A zero-length route has nothing to sample (and would divide 0 / 0 below).
	if (!(effectiveMiles > 0)) return []
	// Interval precedence: explicit override > adaptive-by-length > constant default.
	// The override exists so callers can sample AT A DIFFERENT GRANULARITY than
	// elevation needs — specifically, state detection (an offline polygon lookup
	// with zero network cost) runs at 5mi regardless of route length so thin-state
	// corridors like DE on I-95 or MD panhandle on I-68 aren't skipped on long
	// routes where elevation adaptive-samples at 20-25mi. See Codex 2026-04-19
	// medium finding + route-store.ts for the two-sample-grid pipeline.
	const sampleInterval = intervalMilesOverride ?? adaptiveSamplingInterval(totalDistanceMiles)
	const numIntervals = Math.max(1, Math.ceil(effectiveMiles / sampleInterval))
	const step = effectiveMiles / numIntervals

	const points: Array<{ lng: number; lat: number; distanceMiles: number }> = []
	const searchCursor = { value: 0 }

	for (let i = 0; i <= numIntervals; i++) {
		const target = i === numIntervals ? effectiveMiles : i * step
		const { lng, lat } = interpolateAt(coords, cumulative, target, searchCursor)
		// Report distances against the caller's totalDistanceMiles so the
		// final sample matches the Mapbox-reported total, preserving
		// downstream math contracts.
		const distanceMiles =
			i === numIntervals ? totalDistanceMiles : (target / effectiveMiles) * totalDistanceMiles
		points.push({ lng, lat, distanceMiles })
	}

	return points
}

/**
 * Haversine distance between two lat/lng points in miles.
 */
export function haversineDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
	const R = 3958.8 // Earth radius in miles
	const dLat = ((lat2 - lat1) * Math.PI) / 180
	const dLng = ((lng2 - lng1) * Math.PI) / 180
	const a =
		Math.sin(dLat / 2) * Math.sin(dLat / 2) +
		Math.cos((lat1 * Math.PI) / 180) *
			Math.cos((lat2 * Math.PI) / 180) *
			Math.sin(dLng / 2) *
			Math.sin(dLng / 2)
	return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}
